export const MAX_EXTERNAL_PROJECT_LINKS = 100;
export const MAX_EXTERNAL_URL_LENGTH = 2048;
export const DEFAULT_HRP_BASE_URL = 'https://hrp.mediatropy.com';

export const buildCanonicalHrpProjectUrl = (projectId, baseUrl) => {
    const numericProjectId = Number(projectId);
    if (!Number.isInteger(numericProjectId) || numericProjectId <= 0) return null;

    const configuredBaseUrl = baseUrl ||
        import.meta.env?.VITE_WEBSITE_URL ||
        DEFAULT_HRP_BASE_URL;

    try {
        const url = new URL(configuredBaseUrl);
        if (url.protocol !== 'https:') {
            return `${DEFAULT_HRP_BASE_URL}/project-management/project/${numericProjectId}`;
        }
        url.pathname = `/project-management/project/${numericProjectId}`;
        url.search = '';
        url.hash = '';
        return url.toString().replace(/\/$/, '');
    } catch (_error) {
        return `${DEFAULT_HRP_BASE_URL}/project-management/project/${numericProjectId}`;
    }
};

const normalizeHttpsUrl = (value, label, { required = false } = {}) => {
    const normalized = value === null || value === undefined ? '' : String(value).trim();

    if (!normalized) {
        if (required) throw new Error(`${label} is required`);
        return null;
    }

    if (normalized.length > MAX_EXTERNAL_URL_LENGTH) {
        throw new Error(`${label} must not exceed ${MAX_EXTERNAL_URL_LENGTH} characters`);
    }

    let parsed;
    try {
        parsed = new URL(normalized);
    } catch (_error) {
        throw new Error(`${label} must be a valid URL`);
    }

    if (parsed.protocol !== 'https:') {
        throw new Error(`${label} must use HTTPS`);
    }

    if (parsed.username || parsed.password) {
        throw new Error(`${label} must not contain credentials`);
    }

    return parsed.toString();
};

const normalizeReadOnlyHttpsUrl = (value, label) => {
    try {
        return normalizeHttpsUrl(value, label);
    } catch (_error) {
        return null;
    }
};

export const normalizeEditableExternalProjectLinks = (value) => {
    if (!Array.isArray(value)) return [];
    if (value.length > MAX_EXTERNAL_PROJECT_LINKS) {
        throw new Error(`A project can have at most ${MAX_EXTERNAL_PROJECT_LINKS} related project links`);
    }

    const seenProjectUrls = new Set();
    const seenAtlasUrls = new Set();
    return value.map((link, index) => {
        const projecturl = normalizeHttpsUrl(
            link?.projecturl ?? link?.projectUrl,
            `Tech Platform URL ${index + 1}`
        );
        const atlasurl = normalizeHttpsUrl(
            link?.atlasurl ?? link?.atlasUrl,
            `Atlas URL ${index + 1}`
        );
        const url = normalizeHttpsUrl(link?.url, `Website URL ${index + 1}`);
        const githuburl = normalizeReadOnlyHttpsUrl(
            link?.githuburl ?? link?.githubUrl,
            `GitHub URL ${index + 1}`
        );
        const hrpurl = normalizeReadOnlyHttpsUrl(
            link?.hrpurl ?? link?.hrpUrl,
            `HRP URL ${index + 1}`
        );

        if (!projecturl && !atlasurl) {
            throw new Error(`Connection ${index + 1} requires a Tech Platform URL or Atlas URL`);
        }
        if (projecturl && seenProjectUrls.has(projecturl)) {
            throw new Error(`Connection ${index + 1} is a duplicate`);
        }
        if (atlasurl && seenAtlasUrls.has(atlasurl)) {
            throw new Error(`Connection ${index + 1} is a duplicate`);
        }
        if (projecturl) seenProjectUrls.add(projecturl);
        if (atlasurl) seenAtlasUrls.add(atlasurl);

        return { projecturl, atlasurl, url, githuburl, hrpurl };
    });
};

export const getSafeExternalProjectLinks = (value) => {
    if (!Array.isArray(value)) return [];

    return value.reduce((safeLinks, link) => {
        try {
            const projecturl = normalizeReadOnlyHttpsUrl(
                link?.projecturl ?? link?.projectUrl,
                'Tech Platform URL'
            );
            const atlasurl = normalizeReadOnlyHttpsUrl(
                link?.atlasurl ?? link?.atlasUrl,
                'Atlas URL'
            );
            const url = normalizeReadOnlyHttpsUrl(link?.url, 'Website URL');
            const githuburl = normalizeReadOnlyHttpsUrl(
                link?.githuburl ?? link?.githubUrl,
                'GitHub URL'
            );
            const hrpurl = normalizeReadOnlyHttpsUrl(
                link?.hrpurl ?? link?.hrpUrl,
                'HRP URL'
            );

            if (projecturl || atlasurl) {
                safeLinks.push({ projecturl, atlasurl, url, githuburl, hrpurl });
            }
        } catch (_error) {
            // Invalid project URLs are never rendered as anchors.
        }
        return safeLinks;
    }, []);
};

export { normalizeHttpsUrl };
